// pages/admins/permissions/PermissionModuleCard.jsx
//
// Sections 4-7 from the redesign brief, combined into one collapsible card
// per module: a header showing live Allowed/Denied/Override counts (not
// just a raw permission count), the existing "Set All to Inherit"/"Allow
// All"/"Deny All" bulk actions, and a redesigned permission row --
// left = name + key, middle = Role/User/Effective comparison chips, right =
// a segmented Inherit/Allow/Deny control. All data and every handler
// (state change, bulk actions, immediate save, row editor) are owned by
// AccessManagementUsersPage.jsx and passed in as props -- this component
// renders, it never fetches, saves, or decides access on its own.
import React from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";
import PermissionStateBadge from "./PermissionStateBadge";
import PermissionSourceBadge from "./PermissionSourceBadge";
import {
  STATE_OPTIONS, HIGH_RISK_MODULE_KEYS,
  scopeLabel, shortScopeLabel, scopesForPermission, currentState, cellIsAllowed, moduleCounts,
} from "./permissionUtils";

function RowEditor({ m, p, draftChange, updateDraft, closePopover, commitChange, isSaving }) {
  const isHighRisk = HIGH_RISK_MODULE_KEYS.has(m.moduleKey);
  const reasonRequired = isHighRisk;
  const canApply = draftChange.state !== "ALLOW" || !!draftChange.dataScope;
  return (
    <div className="am-row-editor">
      <div className="am-row-editor-title">New Override for {p.name} — {draftChange.state === "ALLOW" ? "Allow" : "Deny"}</div>

      {draftChange.state === "ALLOW" && (
        <div className="am-pop-row">
          <span>Override Scope (required)</span>
          <Form.Select
            size="sm"
            value={draftChange.dataScope || ""}
            aria-label={`${m.moduleName} - ${p.name} - override scope`}
            onChange={(e) => updateDraft({ dataScope: e.target.value })}
          >
            <option value="">Scope…</option>
            {scopesForPermission(p).map((s) => <option key={s} value={s}>{scopeLabel(s)}</option>)}
          </Form.Select>
        </div>
      )}

      <div className="am-pop-row">
        <span>Reason{reasonRequired ? " (required)" : " (optional)"}</span>
        <Form.Control
          size="sm"
          placeholder={reasonRequired ? "Required for this high-risk module" : "Optional"}
          value={draftChange.reason}
          aria-label={`${m.moduleName} - ${p.name} - reason`}
          onChange={(e) => updateDraft({ reason: e.target.value })}
        />
      </div>
      <div className="am-pop-row am-pop-row-split">
        <div>
          <span>Valid From</span>
          <Form.Control size="sm" type="date" value={draftChange.validFrom ? String(draftChange.validFrom).slice(0, 10) : ""}
            aria-label={`${m.moduleName} - ${p.name} - valid from`}
            onChange={(e) => updateDraft({ validFrom: e.target.value })} />
        </div>
        <div>
          <span>Valid Until</span>
          <Form.Control size="sm" type="date" value={draftChange.validUntil ? String(draftChange.validUntil).slice(0, 10) : ""}
            aria-label={`${m.moduleName} - ${p.name} - valid until`}
            onChange={(e) => updateDraft({ validUntil: e.target.value })} />
        </div>
      </div>

      <div className="am-pop-row am-pop-effective">
        <span>Effective If Applied</span>
        <PermissionStateBadge state={cellIsAllowed(p, draftChange) ? "ALLOWED" : "DENIED"} />
      </div>

      {reasonRequired && !draftChange.reason.trim() && (
        <div className="am-pop-required-hint">A reason is required for this high-risk module.</div>
      )}

      <div className="am-pop-actions">
        <Button variant="link" size="sm" onClick={closePopover} disabled={isSaving}>
          <i className="fa-solid fa-xmark" aria-hidden="true"></i> Cancel
        </Button>
        <Button
          className="unit-btn unit-btn-primary"
          size="sm"
          disabled={isSaving || !canApply || (reasonRequired && !draftChange.reason.trim())}
          onClick={() => commitChange(p, draftChange)}
        >
          {isSaving ? "Saving…" : <><i className="fa-solid fa-check" aria-hidden="true"></i> Apply</>}
        </Button>
      </div>
    </div>
  );
}

function PermissionRow({
  m, p, canManage,
  openPopoverKey, draftChange, updateDraft, closePopover, commitChange,
  rowSaving, rowJustSaved, rowError, onStateChange,
}) {
  const state = currentState(p);
  const rowKey = `${m.id}::${p.id}`;
  const isOpen = openPopoverKey === rowKey;
  const isSaving = rowSaving.has(p.id);
  const justSaved = rowJustSaved.has(p.id);
  const error = rowError.get(p.id);

  return (
    <React.Fragment>
      <tr className={p.override ? "am-row-overridden" : ""} data-permission-name={p.name}>
        {/* ---- left: permission title + its real key as helper text ---- */}
        <td className="am-mt-perm" data-label="Permission">
          <span className="am-mt-perm-name">{p.name}</span>
          <span className="am-mt-perm-key">{m.moduleKey}.{p.key}</span>
        </td>

        {/* ---- middle: Role / User / Effective comparison chips ---- */}
        <td className="pm-compare" data-label="Role vs. User vs. Effective">
          <div className="pm-compare-line">
            <span className="pm-compare-label">Role</span>
            <PermissionStateBadge state={p.roleGranted ? "ALLOWED" : "DENIED"} size="sm" />
            {p.roleGranted && p.roleDataScope && <span className="am-cell-scope-badge">{shortScopeLabel(p.roleDataScope)}</span>}
          </div>
          <div className="pm-compare-line">
            <span className="pm-compare-label">User</span>
            {p.override ? (
              <>
                <PermissionSourceBadge source="OVERRIDE" size="sm" />
                <PermissionStateBadge state={p.override.effect === "ALLOW" ? "ALLOWED" : "DENIED"} size="sm" />
              </>
            ) : (
              <PermissionSourceBadge source="ROLE" label="Inherited" size="sm" />
            )}
          </div>
          <div className="pm-compare-line pm-compare-effective">
            <span className="pm-compare-label">Effective</span>
            <PermissionStateBadge state={p.effectiveAllowed ? "ALLOWED" : "DENIED"} />
            {p.effectiveAllowed && p.effectiveDataScope && <span className="am-cell-scope-badge">{shortScopeLabel(p.effectiveDataScope)}</span>}
          </div>
        </td>

        {/* ---- right: segmented Inherit / Allow / Deny control ---- */}
        <td className="pm-control" data-label="Set Access">
          <div
            className="pm-segmented"
            role="radiogroup"
            aria-label={`${m.moduleName} - ${p.name} - override`}
          >
            {STATE_OPTIONS.map((opt) => (
              <label
                key={opt.key}
                className={`pm-segment ${opt.cls} ${state.state === opt.key ? "pm-segment-checked" : ""} ${!canManage ? "pm-segment-disabled" : ""}`}
              >
                <input
                  type="radio"
                  name={`override-${p.id}`}
                  value={opt.key}
                  checked={state.state === opt.key}
                  disabled={!canManage || isSaving}
                  onChange={() => onStateChange(m, p, opt.key)}
                />
                <i className={`fa-solid ${opt.icon}`} aria-hidden="true"></i>
                {opt.label}
              </label>
            ))}
          </div>
          <div className="am-row-feedback">
            {isSaving && <span className="am-row-saving"><Spinner animation="border" size="sm" /> Saving…</span>}
            {!isSaving && justSaved && <span className="am-row-saved"><i className="fa-solid fa-check" aria-hidden="true"></i> Saved</span>}
            {!isSaving && error && <span className="am-row-error-text">{error}</span>}
          </div>
        </td>
      </tr>
      {isOpen && (
        <tr className="am-row-editor-tr">
          <td colSpan={3}>
            <RowEditor
              m={m} p={p}
              draftChange={draftChange}
              updateDraft={updateDraft}
              closePopover={closePopover}
              commitChange={commitChange}
              isSaving={isSaving}
            />
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}

export default function PermissionModuleCard({
  m, isExpanded, onToggleExpand, canManage, bulkSaving,
  onSetAllInherit, onRequestBulk,
  visibleRows,
  openPopoverKey, draftChange, updateDraft, closePopover, commitChange,
  rowSaving, rowJustSaved, rowError, onStateChange,
}) {
  const isHighRisk = HIGH_RISK_MODULE_KEYS.has(m.moduleKey);
  const counts = moduleCounts(m.permissions);

  return (
    <div className="am-user-module-group pm-module-card">
      <div className="am-module-header-row">
        <button
          type="button"
          className="am-expand-btn am-module-header"
          onClick={onToggleExpand}
          aria-expanded={isExpanded}
        >
          {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
          <span className="am-module-header-title">{m.moduleName}</span>
          {isHighRisk && <span className="am-high-risk-tag">High risk</span>}
        </button>
        <div className="pm-module-counts">
          <span className="pm-count-chip pm-count-total" title="Total permissions in this module">{counts.total} total</span>
          <span className="pm-count-chip pm-count-allowed" title="Effectively allowed">{counts.allowed} allowed</span>
          <span className="pm-count-chip pm-count-denied" title="Effectively denied">{counts.denied} denied</span>
          {counts.overrides > 0 && (
            <span className="pm-count-chip pm-count-override" title="Permissions with a user-specific override">{counts.overrides} override{counts.overrides === 1 ? "" : "s"}</span>
          )}
        </div>
        {isExpanded && canManage && (
          <div className="am-module-bulk-actions">
            <Button
              className="unit-btn unit-btn-light"
              size="sm"
              disabled={counts.overrides === 0 || bulkSaving}
              onClick={onSetAllInherit}
            >
              Reset to Inherited
            </Button>
            <Button
              className="unit-btn unit-btn-light am-bulk-allow-btn"
              size="sm"
              disabled={bulkSaving}
              onClick={() => onRequestBulk("ALLOW_ALL")}
            >
              Allow All
            </Button>
            <Button
              className="unit-btn unit-btn-light am-bulk-deny-btn"
              size="sm"
              disabled={bulkSaving}
              onClick={() => onRequestBulk("DENY_ALL")}
            >
              Deny All
            </Button>
          </div>
        )}
      </div>

      {isExpanded && (
        <div className="am-matrix-table-wrapper">
          {visibleRows.length === 0 && <div className="am-empty am-matrix-empty">No permissions match the current filter.</div>}
          {visibleRows.length > 0 && (
            <table className="am-matrix-table pm-matrix-table">
              <thead>
                <tr>
                  <th className="am-mt-perm">Permission</th>
                  <th className="pm-compare">Role · User · Effective</th>
                  <th className="pm-control">Set Access</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((p) => (
                  <PermissionRow
                    key={p.id}
                    m={m} p={p} canManage={canManage}
                    openPopoverKey={openPopoverKey}
                    draftChange={draftChange}
                    updateDraft={updateDraft}
                    closePopover={closePopover}
                    commitChange={commitChange}
                    rowSaving={rowSaving}
                    rowJustSaved={rowJustSaved}
                    rowError={rowError}
                    onStateChange={onStateChange}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
